import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { OarTaskService } from '../../services/oar-task-service';
import { ClaimAnalysisResultModel } from '../../models/claim-analysis-result-model';
import { OarStatementContentModel } from '../../models/oar-statement-content-model';
import type { ClaimAnalysisResult } from '../../types/claim-analysis';
import type { StatementComparisonOptions } from '../../services/oar-task-service';
import type { ClaimAnalysisCommandFactory } from '../claim-analysis-command';
import { AnalyzeClaimsCommandImpl, type AnalyzeClaimsCommandParams } from './analyze-claims-command';
import { GenerateStatementContentCommandImpl, type GenerateStatementContentCommandParams } from './generate-statement-content-command';
import { GenerateComparisonPageCommandImpl, type GenerateComparisonPageCommandParams } from './generate-comparison-page-command';

/**
 * 权利要求分析命令工厂实现
 *
 * 负责创建各种命令实例，将服务、模型等依赖注入到命令中
 */
@injectable()
export class ClaimAnalysisCommandFactoryImpl implements ClaimAnalysisCommandFactory {
    constructor(
        @inject(OarTaskService)
        private _oarTaskService: OarTaskService,
        @inject(ClaimAnalysisResultModel)
        private _claimAnalysisResultModel: ClaimAnalysisResultModel,
        @inject(OarStatementContentModel)
        private _oarStatementContentModel: OarStatementContentModel,
    ) {}

    /**
     * 创建分析权利要求命令
     *
     * @param taskId - 任务 ID
     * @param conclusiveOpinionId - 结论性意见 ID
     * @returns 分析命令实例
     */
    createAnalyzeCommand(taskId: string, conclusiveOpinionId: string): import('../claim-analysis-command').AnalyzeClaimsCommand {
        const params: AnalyzeClaimsCommandParams = { taskId, conclusiveOpinionId };
        return new AnalyzeClaimsCommandImpl(
            this._oarTaskService,
            this._claimAnalysisResultModel,
            params,
        );
    }

    /**
     * 创建生成陈述书内容命令
     *
     * @param taskId - 任务 ID
     * @param claimAnalysisResults - 权利要求分析结果列表
     * @returns 生成陈述书命令实例
     */
    createGenerateStatementCommand(
        taskId: string,
        claimAnalysisResults: ClaimAnalysisResult[],
    ): import('../claim-analysis-command').GenerateStatementContentCommand {
        const params: GenerateStatementContentCommandParams = { taskId, claimAnalysisResults };
        return new GenerateStatementContentCommandImpl(
            this._oarTaskService,
            this._oarStatementContentModel,
            params,
        );
    }

    /**
     * 创建生成对照页内容命令
     *
     * @param taskId - 任务 ID
     * @param options - 对照页生成选项
     * @returns 生成对照页命令实例
     */
    createGenerateComparisonCommand(
        taskId: string,
        options: StatementComparisonOptions,
    ): import('../claim-analysis-command').GenerateComparisonPageCommand {
        const params: GenerateComparisonPageCommandParams = { taskId, options };
        return new GenerateComparisonPageCommandImpl(
            this._oarTaskService,
            this._oarStatementContentModel,
            params,
        );
    }
}
